// The round-start roll call: before the countdown, every player is introduced
// in turn, leader first (the client shows each head fading in with a name tag,
// and lights the player's row on the scoreboard). The leader gets the longest
// beat and every following player comes a little sooner, so the call gathers
// pace down the table; for a big roster the gaps are scaled so the introductions
// still fit ROLL_CALL_INTRO_MAX. Once the last player is on, everyone stays on
// stage together for ROLL_CALL_LINGER seconds, then the name tags fade out over
// ROLL_CALL_FADE seconds, and the countdown proper starts the moment they are
// gone. Those two are THE knobs for "how long do the names stay up": the
// linger is the same for any roster size and any game mode. The start phase is the roll call plus the
// countdown proper, so worms only set off once everyone has been introduced.
//
// ONE timeline, in seconds into the start phase, for a roster of n:
//
//   0 - FIRST - p1 - gap - p2 - ... - pN - LINGER - [fade FADE] - | countdown
//                                          ^ all on stage       ^ rollCallDuration
//
// rollCallSchedule(n) is the single source of it: the server sizes the start
// phase with its `duration`, the client times every beat (heads, tags, rail
// rows, the tags' fade) from the same object, so the two can never disagree,
// and no number below is ever repeated elsewhere. Every knob can be tuned on
// its own: the linger and the fade are fixed beats added after the introductions,
// so they never eat into them, and the roster scale is clamped so an odd
// combination (a huge FIRST, a tiny INTRO_MAX) degrades to "everyone at once"
// instead of running backwards. roll-call-spec.js pins these guarantees.
// (2026-10-23, the feel pass: 2.5 s in all, down from about 6, so the countdown
// starts 7 s after a Play Now round ends, its arena showing at 4.5 s)
export var ROLL_CALL_FIRST = 0.15;      // seconds to the first player (the leader)
export var ROLL_CALL_GAP_FIRST = 0.5;   // the leader's beat, before the second player (they get the stage to themselves)
export var ROLL_CALL_GAP_DECAY = 0.82;  // each further gap is this much of the previous
export var ROLL_CALL_GAP_MIN = 0.12;    // but never shorter than this
export var ROLL_CALL_INTRO_MAX = 1.3;   // the introductions (start to the last beat), at most; bigger rosters are squeezed into it
export var ROLL_CALL_LINGER = 0.9;      // seconds ALL names stay up after the last one has appeared ...
export var ROLL_CALL_FADE = 0.3;        // ... then they fade out over this long; the countdown starts as the fade ends

// Seconds from the first player's beat to the i-th player's (0-based), before
// any scaling for a big roster.
function rawOffset(i) {
    var t = 0, gap = ROLL_CALL_GAP_FIRST;
    for (var k = 0; k < i; k++) {
        t += gap;
        gap = Math.max(ROLL_CALL_GAP_MIN, gap * ROLL_CALL_GAP_DECAY);
    }
    return t;
}

// Scale that squeezes a roster of n into ROLL_CALL_INTRO_MAX when its gaps
// overrun it. Never negative: a room of 0 puts everyone on the first beat.
function rosterScale(n) {
    var span = rawOffset(n - 1);
    var room = Math.max(0, ROLL_CALL_INTRO_MAX - ROLL_CALL_FIRST);
    return span > room ? room / span : 1;
}

// The whole timeline for a roster of n players:
//   delays   - seconds into the start phase at which player i (0-based, in
//              introduction order) is introduced; non-decreasing
//   lastBeat - the last player's beat
//   fadeAt   - when the tags start fading out (lastBeat + LINGER)
//   fade     - how long that fade takes (FADE)
//   duration - the roll call's length; the countdown proper starts here
// An empty roster (or a game without a roll call) is all zeros.
export function rollCallSchedule(n) {
    n = Math.max(0, Math.floor(n || 0));
    if (n === 0) {
        return { delays: [], lastBeat: 0, fadeAt: 0, fade: 0, duration: 0 };
    }
    var scale = rosterScale(n);
    var delays = [];
    for (var i = 0; i < n; i++) {
        delays.push(ROLL_CALL_FIRST + rawOffset(i) * scale);
    }
    var lastBeat = delays[n - 1];
    var fadeAt = lastBeat + ROLL_CALL_LINGER;
    return { delays: delays, lastBeat: lastBeat, fadeAt: fadeAt, fade: ROLL_CALL_FADE, duration: fadeAt + ROLL_CALL_FADE };
}

// How many beats a roster takes: players tied for the lead coming into the
// round are introduced TOGETHER on the first beat (they are all "the leader"),
// so k tied leaders take one beat, not k. A top everyone shares (round 1, or a
// full tie) has no leader, and every player gets their own beat. `score` is the
// round's start score by player id; a missing score counts as 0.
export function rollCallBeats(playerIds, score) {
    var ids = playerIds || [];
    var n = ids.length;
    if (n < 2) { return n; }
    var scores = ids.map(function (id) { return (score && score[id]) || 0; });
    var top = Math.max.apply(null, scores);
    var leaders = scores.filter(function (s) { return s === top; }).length;
    if (leaders === n) { return n; } // everyone tied: no leader
    return n - (leaders - 1);
}

// Seconds into the start phase at which the i-th player (0-based) is introduced.
export function rollCallDelay(n, i) {
    var s = rollCallSchedule(n);
    if (s.delays.length === 0) { return 0; }
    return s.delays[Math.min(Math.max(0, i), s.delays.length - 1)];
}

// How long the roll call takes for a roster of n (the linger and the tags'
// fade-out included).
export function rollCallDuration(n) {
    return rollCallSchedule(n).duration;
}
